import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, Edit, Info, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import {
  ExternalSystemAdmin,
  UpdateExternalSystemAdminRequest,
  EXTERNAL_SYSTEM_PROVIDERS,
  DOC_TYPE_BITS
} from '../../../../types/externalSystem';
import { externalSystemService } from '../../../../services/externalSystemService';
import { userProfileService } from '../../../../services/userProfileService';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import type { ColumnDefinition, TableAction } from '../../../shared/table/types/table';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface ExternalSystemAppProps {
  onClose?: () => void;
  onBack?: () => void;
}

interface ExternalSystemFormData {
  externalSystemID: string;
  externalCustomID: string;
  description1: string;
  operatorID: string;
  providerKey: string;
  apiBaseUrl: string;
  apiToken: string;
  clearToken: boolean;
  importPListStatus: string;
  pickExport: boolean;
  pickConfirmation: boolean;
  activeYesNo: boolean;
  confirmationNextId: string;
}

const emptyFormData: ExternalSystemFormData = {
  externalSystemID: '',
  externalCustomID: '',
  description1: '',
  operatorID: '',
  providerKey: '',
  apiBaseUrl: '',
  apiToken: '',
  clearToken: false,
  importPListStatus: '45',
  pickExport: true,
  pickConfirmation: true,
  activeYesNo: false,
  confirmationNextId: '0'
};

/** Sammenleggbar hjelpeseksjon — forklarer hva et eksternt system er og hvordan rutingen virker
 *  (mønster fra ReportConfigurationList.HelpSection). */
const HelpSection: React.FC = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-100 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 text-blue-800">
          <Info size={18} />
          <span className="font-medium text-sm">{t('manager.modules.externalSystems.help.title', {}, 'Hvordan fungerer vareheis-integrasjonen?')}</span>
        </div>
        {expanded ? <ChevronDown size={18} className="text-blue-600" /> : <ChevronRight size={18} className="text-blue-600" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 text-sm text-blue-900 space-y-3 max-h-64 overflow-y-auto">
          <div>
            <p className="font-semibold mb-1">{t('manager.modules.externalSystems.help.whatTitle', {}, 'Hva er et eksternt system?')}</p>
            <p>
              {t('manager.modules.externalSystems.help.whatBody', {}, 'Hver rad er en kobling til et automatlager — typisk en vareheis (SHARK). Plukklister med varer som hører til heisen sendes automatisk dit når plukklisten godkjennes, plukkes av operatøren på heisens egen skjerm, og ferdigmeldes automatisk tilbake i Compass med lagerbevegelser og statusoppdatering. Plukklisten er låst så lenge heisen jobber med den.')}
            </p>
          </div>

          <div>
            <p className="font-semibold mb-1">{t('manager.modules.externalSystems.help.routingTitle', {}, 'Hvordan vet Compass hvilke varer som ligger i heisen?')}</p>
            <ol className="list-decimal list-inside space-y-0.5 ml-2">
              <li>{t('manager.modules.externalSystems.help.routing1', {}, 'Varens lokasjonsgruppe (BinGrpNo på varen) peker på en lokasjonsgruppe (StcBinGrp) der «Eksternt system» er satt til System-ID-en i denne tabellen.')}</li>
              <li>{t('manager.modules.externalSystems.help.routing2', {}, 'Regelmotoren merker ordrelinjene ved plukkgenerering og samler heis-linjer på egne plukklister (grupperingsuttrykket i plukkregelen må inneholde ExternalSystemID).')}</li>
              <li>{t('manager.modules.externalSystems.help.routing3', {}, 'Ved godkjenning sendes plukklisten til heisen — deretter går alt automatisk.')}</li>
            </ol>
          </div>

          <div>
            <p className="font-semibold mb-1">{t('manager.modules.externalSystems.help.fieldsTitle', {}, 'Feltene')}</p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li><strong>{t('manager.modules.externalSystems.help.fieldProvider', {}, 'Provider')}</strong> — {t('manager.modules.externalSystems.help.fieldProviderBody', {}, 'hvilket heis-system det kobles mot: «Shark» = SHARK WMS (Logiware), «MockLift» = innebygd testheis som auto-bekrefter (kun til test).')}</li>
              <li><strong>{t('manager.modules.externalSystems.help.fieldUrl', {}, 'API-URL og API-token')}</strong> — {t('manager.modules.externalSystems.help.fieldUrlBody', {}, 'tilkoblingen til heisens API (fås fra leverandøren). Tokenet vises aldri igjen etter lagring.')}</li>
              <li><strong>{t('manager.modules.externalSystems.help.fieldFlows', {}, 'Flyter')}</strong> — {t('manager.modules.externalSystems.help.fieldFlowsBody', {}, '«Plukk ut» sender plukkordrer til heisen, «Plukk inn» henter ferdigmeldingene. Normalt skal begge være på.')}</li>
              <li><strong>{t('manager.modules.externalSystems.help.fieldCompletion', {}, 'Avslutning')}</strong> — {t('manager.modules.externalSystems.help.fieldCompletionBody', {}, 'hva som skjer når heisen er ferdig: 45 = plukklisten går til pakking (krever konfigurert pakkelokasjon), 50 = avsluttes direkte med eksport til ERP/TA.')}</li>
              <li><strong>{t('manager.modules.externalSystems.help.fieldOperator', {}, 'Heis-operatør')}</strong> — {t('manager.modules.externalSystems.help.fieldOperatorBody', {}, 'profilen som stemples som plukker og låse-eier på heis-plukk (må finnes som profil).')}</li>
              <li><strong>{t('manager.modules.externalSystems.help.fieldWatermark', {}, 'Vannmerke')}</strong> — {t('manager.modules.externalSystems.help.fieldWatermarkBody', {}, 'hvor langt i heisens bekreftelsesstrøm Compass har lest. Vedlikeholdes automatisk — juster kun ved feilsøking.')}</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold mb-1">{t('manager.modules.externalSystems.help.prereqTitle', {}, 'Forutsetninger')}</p>
            <p>
              {t('manager.modules.externalSystems.help.prereqBody', {}, 'Hovedbryteren «Vareheis aktivert» må være på under Systeminnstillinger → Vareheis, og raden her må være «Aktiv». Er begge av, sendes ingenting — konfigurasjonen kan trygt forberedes på forhånd.')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Vedlikehold av ExternalSystem (vareheis-/eksternsystem-konfig, TW 36367005).
 * Bygget etter LoadingZoneApp-mønsteret (WarehouseTable + modal). Radene styrer
 * VerticalLiftWorker: ProviderKey/ApiBaseUrl/ApiToken = tilkobling, DocTypeActive =
 * aktive flyter (bit 1/2 i fase 1), ImportPListStatus = avslutningsrute (45/50),
 * ConfirmationNextId = polling-vannmerket. API-token vises aldri — kun satt/ikke satt.
 */
export const ExternalSystemApp: React.FC<ExternalSystemAppProps> = ({ onClose, onBack }) => {
  const { t } = useTranslation();
  const [systems, setSystems] = useState<ExternalSystemAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingHasToken, setEditingHasToken] = useState(false);
  const [formData, setFormData] = useState<ExternalSystemFormData>(emptyFormData);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [profileOptions, setProfileOptions] = useState<{ userID: string; label: string }[]>([]);

  const loadSystems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await externalSystemService.getExternalSystems();
      setSystems(data);
      // Rydd bort valg som ikke lenger finnes i dataene (ellers henger f.eks. «Slett (2)» igjen).
      setSelectedIds((prev) => prev.filter((id) => data.some((s) => String(s.id) === id)));
    } catch (err) {
      console.error('Error loading external systems:', err);
      setError(t('manager.modules.externalSystems.errors.loadFailed', {}, 'Feil ved lasting av eksterne systemer'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSystems();
  }, [loadSystems]);

  // Profiler til heis-operatør-nedtrekket. Verdien som lagres er Profile.UserID —
  // eneste gyldige brukernøkkel etter Operator-tabell-utfasingen (Profile.Operator = UserID).
  useEffect(() => {
    (async () => {
      try {
        const profiles = await userProfileService.getAllProfilesForCompany();
        setProfileOptions(
          profiles
            .map((p) => ({ userID: p.userID, label: p.operator && p.operator !== p.userID ? `${p.userID} — ${p.operator}` : p.userID }))
            .sort((a, b) => a.userID.localeCompare(b.userID, 'no-NO'))
        );
      } catch (err) {
        console.error('Error loading profiles for operator dropdown:', err);
        setProfileOptions([]);
      }
    })();
  }, []);

  const handleSelectionChange = useCallback((ids: (string | number)[], _data: ExternalSystemAdmin[]) => {
    setSelectedIds(ids.map((id) => String(id)));
  }, []);

  // Streng-ID hele veien — selectedIds er strenger, og tabellens Set.has() skiller på type.
  const getRowId = useCallback((item: ExternalSystemAdmin) => String(item.id), []);

  // Radklikk = veksle valg (checkboksen er liten på nettbrett — hele raden skal være trykkmål).
  const handleRowClick = useCallback((row: ExternalSystemAdmin) => {
    const id = String(row.id);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const openCreate = useCallback(() => {
    setEditingId(null);
    setEditingHasToken(false);
    setFormData(emptyFormData);
    setFormError(null);
    setSuccessMessage(null);
    setShowFormModal(true);
  }, []);

  const openEdit = useCallback(() => {
    if (selectedIds.length !== 1) return;
    const row = systems.find((s) => String(s.id) === selectedIds[0]);
    if (!row) return;
    setEditingId(row.id);
    setEditingHasToken(row.hasApiToken);
    setFormData({
      externalSystemID: String(row.externalSystemID),
      externalCustomID: row.externalCustomID ?? '',
      description1: row.description1 ?? '',
      operatorID: row.operatorID ?? '',
      providerKey: row.providerKey ?? '',
      apiBaseUrl: row.apiBaseUrl ?? '',
      apiToken: '',
      clearToken: false,
      importPListStatus: String(row.importPListStatus ?? 45),
      pickExport: (row.docTypeActive & DOC_TYPE_BITS.pickExport) !== 0,
      pickConfirmation: (row.docTypeActive & DOC_TYPE_BITS.pickConfirmation) !== 0,
      activeYesNo: row.activeYesNo,
      confirmationNextId: String(row.confirmationNextId)
    });
    setFormError(null);
    setSuccessMessage(null);
    setShowFormModal(true);
  }, [selectedIds, systems]);

  const handleDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(
      t('manager.modules.externalSystems.confirmDelete', { '0': selectedIds.length }, `Slette ${selectedIds.length} eksternt system(er)? Ruting via lokasjonsgrupper som peker hit slutter å virke.`)
    );
    if (!confirmed) return;
    try {
      for (const id of selectedIds) {
        await externalSystemService.deleteExternalSystem(parseInt(id, 10));
      }
      await loadSystems();
      setSelectedIds([]);
    } catch (err) {
      console.error('Error deleting external systems:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.externalSystems.errors.deleteFailed', {}, 'Feil ved sletting av eksternt system'));
    }
  }, [selectedIds, loadSystems, t]);

  const columnFactory = createColumnFactory<ExternalSystemAdmin>();

  const docTypeLabel = useCallback((docTypeActive: number): string => {
    const parts: string[] = [];
    if (docTypeActive & DOC_TYPE_BITS.pickExport) parts.push(t('manager.modules.externalSystems.docTypes.pickExport', {}, 'Plukk ut'));
    if (docTypeActive & DOC_TYPE_BITS.pickConfirmation) parts.push(t('manager.modules.externalSystems.docTypes.pickConfirmation', {}, 'Plukk inn'));
    const other = docTypeActive & ~(DOC_TYPE_BITS.pickExport | DOC_TYPE_BITS.pickConfirmation);
    if (other) parts.push(`+${other}`);
    return parts.length > 0 ? parts.join(', ') : '—';
  }, [t]);

  const columns: ColumnDefinition<ExternalSystemAdmin>[] = useMemo(() => [
    columnFactory.createNumberColumn('externalSystemID', 'externalSystemID', t('manager.modules.externalSystems.columns.externalSystemID', {}, 'System-ID'), {
      width: 100, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('externalCustomID', 'externalCustomID', t('manager.modules.externalSystems.columns.externalCustomID', {}, 'Navn'), {
      width: 130, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 200, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('providerKey', 'providerKey', t('manager.modules.externalSystems.columns.providerKey', {}, 'Provider'), {
      width: 110, sortable: true,
      filterOptions: EXTERNAL_SYSTEM_PROVIDERS.map((p) => ({ value: p, label: p }))
    }),
    columnFactory.createTextColumn('activeYesNo', 'activeYesNo', t('manager.modules.externalSystems.columns.active', {}, 'Aktiv'), {
      width: 80, sortable: true,
      formatter: (value: any) => (value ? t('common.labels.yes', {}, 'Ja') : t('common.labels.no', {}, 'Nei')),
      filterOptions: [
        { value: 'true', label: t('common.labels.yes', {}, 'Ja') },
        { value: 'false', label: t('common.labels.no', {}, 'Nei') }
      ]
    }),
    columnFactory.createTextColumn('docTypeActive', 'docTypeActive', t('manager.modules.externalSystems.columns.docTypes', {}, 'Flyter'), {
      width: 160,
      formatter: (value: any) => docTypeLabel(Number(value ?? 0)),
      filterOptions: [
        { value: '3', label: `${t('manager.modules.externalSystems.docTypes.pickExport', {}, 'Plukk ut')}, ${t('manager.modules.externalSystems.docTypes.pickConfirmation', {}, 'Plukk inn')}` },
        { value: '1', label: t('manager.modules.externalSystems.docTypes.pickExport', {}, 'Plukk ut') },
        { value: '2', label: t('manager.modules.externalSystems.docTypes.pickConfirmation', {}, 'Plukk inn') }
      ]
    }),
    columnFactory.createNumberColumn('importPListStatus', 'importPListStatus', t('manager.modules.externalSystems.columns.importPListStatus', {}, 'Avslutning'), {
      width: 130, sortable: true,
      formatter: (value: any) =>
        value === 45 ? t('manager.modules.externalSystems.fields.status45', {}, '45 — Til pakking')
        : value === 50 ? t('manager.modules.externalSystems.fields.status50', {}, '50 — Klar for eksport (direkteavslutning)')
        : value != null ? String(value) : '',
      filterOptions: [
        { value: '45', label: t('manager.modules.externalSystems.fields.status45', {}, '45 — Til pakking') },
        { value: '50', label: t('manager.modules.externalSystems.fields.status50', {}, '50 — Klar for eksport (direkteavslutning)') }
      ]
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', t('manager.modules.externalSystems.columns.operatorID', {}, 'Operatør'), {
      width: 110, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('apiBaseUrl', 'apiBaseUrl', t('manager.modules.externalSystems.columns.apiBaseUrl', {}, 'API-URL'), {
      width: 220, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('hasApiToken', 'hasApiToken', t('manager.modules.externalSystems.columns.hasApiToken', {}, 'Token'), {
      width: 80,
      formatter: (value: any) => (value ? '••••' : '—')
    }),
    columnFactory.createNumberColumn('confirmationNextId', 'confirmationNextId', t('manager.modules.externalSystems.columns.confirmationNextId', {}, 'Vannmerke'), {
      width: 100, sortable: true, filterType: 'text'
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminDelivery.common.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 130, sortable: true, filterType: 'text'
    })
  ], [t, docTypeLabel]);

  const tableActions: TableAction<ExternalSystemAdmin>[] = useMemo(() => [
    {
      id: 'create-externalsystem',
      label: t('manager.modules.externalSystems.actions.createNew', {}, 'Opprett ny'),
      icon: Plus,
      onClick: openCreate,
      variant: 'primary' as const
    },
    {
      id: 'edit-externalsystem',
      label: t('common.buttons.edit', {}, 'Rediger'),
      icon: Edit,
      onClick: openEdit,
      disabled: () => selectedIds.length !== 1,
      variant: 'primary' as const
    },
    {
      id: 'delete-externalsystem',
      label: t('manager.modules.adminDelivery.common.delete', { '0': selectedIds.length }, `Slett (${selectedIds.length})`),
      icon: Trash2,
      onClick: handleDelete,
      disabled: () => selectedIds.length === 0,
      variant: 'secondary' as const
    },
    {
      id: 'refresh-externalsystems',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadSystems(),
      variant: 'secondary' as const
    }
  ], [selectedIds.length, openCreate, openEdit, handleDelete, loadSystems, t]);

  const handleInputChange = (field: keyof ExternalSystemFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const buildDocTypeActive = (): number =>
    (formData.pickExport ? DOC_TYPE_BITS.pickExport : 0) |
    (formData.pickConfirmation ? DOC_TYPE_BITS.pickConfirmation : 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const systemIdNum = parseInt(formData.externalSystemID.trim(), 10);
    if (!formData.externalSystemID.trim() || isNaN(systemIdNum) || systemIdNum <= 0) {
      setFormError(t('manager.modules.externalSystems.errors.idRequired', {}, 'System-ID (positivt tall) er påkrevd'));
      return;
    }
    if (formData.activeYesNo && formData.providerKey && !formData.apiBaseUrl.trim() && formData.providerKey !== 'MockLift') {
      setFormError(t('manager.modules.externalSystems.errors.urlRequired', {}, 'API-URL er påkrevd for aktiv provider (unntatt MockLift)'));
      return;
    }

    setFormLoading(true);
    setFormError(null);
    try {
      if (editingId == null) {
        await externalSystemService.createExternalSystem({
          externalSystemID: systemIdNum,
          externalCustomID: formData.externalCustomID.trim() || undefined,
          description1: formData.description1.trim() || undefined,
          operatorID: formData.operatorID.trim() || undefined,
          importPListStatus: parseInt(formData.importPListStatus, 10),
          activeYesNo: formData.activeYesNo,
          docTypeActive: buildDocTypeActive(),
          providerKey: formData.providerKey || undefined,
          apiBaseUrl: formData.apiBaseUrl.trim() || undefined,
          apiToken: formData.apiToken.trim() || undefined
        });
        setSuccessMessage(t('manager.modules.externalSystems.create.success', {}, 'Nytt eksternt system ble opprettet'));
      } else {
        const request: UpdateExternalSystemAdminRequest = {
          externalSystemID: systemIdNum,
          externalCustomID: formData.externalCustomID.trim(),
          description1: formData.description1.trim(),
          operatorID: formData.operatorID.trim(),
          importPListStatus: parseInt(formData.importPListStatus, 10),
          activeYesNo: formData.activeYesNo,
          docTypeActive: buildDocTypeActive(),
          providerKey: formData.providerKey,
          apiBaseUrl: formData.apiBaseUrl.trim(),
          confirmationNextId: parseInt(formData.confirmationNextId, 10) || 0
        };
        // Token: kun med i requesten når det skal endres — utelatt = behold eksisterende.
        if (formData.clearToken) {
          request.apiToken = '';
        } else if (formData.apiToken.trim()) {
          request.apiToken = formData.apiToken.trim();
        }
        await externalSystemService.updateExternalSystem(editingId, request);
        setSuccessMessage(t('manager.modules.externalSystems.update.success', {}, 'Eksternt system ble oppdatert'));
      }
      setTimeout(() => {
        loadSystems();
        setSelectedIds([]);
        setShowFormModal(false);
        setSuccessMessage(null);
      }, 800);
    } catch (err) {
      console.error('Error saving external system:', err);
      setFormError(err instanceof Error ? err.message : t('manager.modules.externalSystems.errors.saveFailed', {}, 'Feil ved lagring av eksternt system'));
    } finally {
      setFormLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {t('manager.modules.externalSystems.title', {}, 'Vareheis og automatlager')}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('manager.modules.externalSystems.subtitle', {}, 'Koblinger til eksterne plukk-systemer — plukklister med heis-varer sendes og ferdigmeldes automatisk')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button onClick={onClose} className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600">
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          )}
        </div>
      </div>

      {/* Hjelp */}
      <div className="px-4 pt-3">
        <HelpSection />
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={systems}
          columns={columns}
          loading={loading}
          error={error || undefined}
          config={{
            persistenceKey: 'externalsystem-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 100,
            compactMode: true,
            stickyHeader: true,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: { mode: 'multi', selectAllMode: 'current-page' }
          }}
          selectedRows={selectedIds}
          onSelectionChange={handleSelectionChange}
          actions={{ onRowClick: handleRowClick }}
          getRowId={getRowId}
          tableActions={tableActions}
          aria-label={t('manager.modules.externalSystems.tableAriaLabel', {}, 'Eksterne systemer tabell')}
        />
      </div>

      {/* Create/Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId == null
                  ? t('manager.modules.externalSystems.create.title', {}, 'Opprett nytt eksternt system')
                  : t('manager.modules.externalSystems.update.title', {}, 'Rediger eksternt system')}
              </h2>
              <button onClick={() => setShowFormModal(false)} className="text-gray-400 hover:text-gray-600 text-xl" aria-label={t('manager.modules.adminDelivery.common.closeModal', {}, 'Lukk modal')}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              )}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm">{successMessage}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('manager.modules.externalSystems.fields.externalSystemID', {}, 'System-ID *')}</label>
                  <input type="number" value={formData.externalSystemID}
                    onChange={(e) => handleInputChange('externalSystemID', e.target.value)}
                    disabled={formLoading} className={inputClass} min={1} required />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manager.modules.externalSystems.fields.externalSystemIDHint', {}, 'Nøkkelen lokasjonsgrupper (StcBinGrp) ruter mot')}
                  </p>
                </div>
                <div>
                  <label className={labelClass}>{t('manager.modules.externalSystems.fields.externalCustomID', {}, 'Navn')}</label>
                  <input type="text" value={formData.externalCustomID}
                    onChange={(e) => handleInputChange('externalCustomID', e.target.value)}
                    disabled={formLoading} className={inputClass} maxLength={50} placeholder="SHARK1" />
                </div>
              </div>

              <div>
                <label className={labelClass}>{t('common.fields.description', {}, 'Beskrivelse')}</label>
                <input type="text" value={formData.description1}
                  onChange={(e) => handleInputChange('description1', e.target.value)}
                  disabled={formLoading} className={inputClass} maxLength={255} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('manager.modules.externalSystems.fields.providerKey', {}, 'Provider')}</label>
                  <select value={formData.providerKey}
                    onChange={(e) => handleInputChange('providerKey', e.target.value)}
                    disabled={formLoading} className={inputClass}>
                    <option value="">{t('manager.modules.externalSystems.fields.providerNone', {}, '(ingen — ikke API-aktiv)')}</option>
                    {EXTERNAL_SYSTEM_PROVIDERS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('manager.modules.externalSystems.fields.operatorID', {}, 'Heis-operatør')}</label>
                  <select value={formData.operatorID}
                    onChange={(e) => handleInputChange('operatorID', e.target.value)}
                    disabled={formLoading} className={inputClass}>
                    <option value="">{t('manager.modules.externalSystems.fields.operatorNone', {}, '(ingen)')}</option>
                    {/* Behold en lagret verdi som ikke (lenger) finnes blant profilene, så den ikke mistes ved lagring */}
                    {formData.operatorID && !profileOptions.some((p) => p.userID === formData.operatorID) && (
                      <option value={formData.operatorID}>{formData.operatorID} (!)</option>
                    )}
                    {profileOptions.map((p) => (
                      <option key={p.userID} value={p.userID}>{p.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manager.modules.externalSystems.fields.operatorIDHint', {}, 'Profilen (UserID) som stemples som plukker og låse-eier på heis-plukk')}
                  </p>
                </div>
              </div>

              <div>
                <label className={labelClass}>{t('manager.modules.externalSystems.fields.apiBaseUrl', {}, 'API-URL')}</label>
                <input type="text" value={formData.apiBaseUrl}
                  onChange={(e) => handleInputChange('apiBaseUrl', e.target.value)}
                  disabled={formLoading} className={inputClass} maxLength={255}
                  placeholder="https://restapi.sharkwms.com/1.3" />
              </div>

              <div>
                <label className={labelClass}>
                  {t('manager.modules.externalSystems.fields.apiToken', {}, 'API-token')}
                  {editingId != null && editingHasToken && !formData.clearToken && (
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {t('manager.modules.externalSystems.fields.apiTokenSet', {}, '(satt — la stå tomt for å beholde)')}
                    </span>
                  )}
                </label>
                <input type="password" value={formData.apiToken}
                  onChange={(e) => handleInputChange('apiToken', e.target.value)}
                  disabled={formLoading || formData.clearToken} className={inputClass} maxLength={1024}
                  autoComplete="new-password" />
                {editingId != null && editingHasToken && (
                  <label className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                    <input type="checkbox" checked={formData.clearToken}
                      onChange={(e) => handleInputChange('clearToken', e.target.checked)}
                      disabled={formLoading} />
                    {t('manager.modules.externalSystems.fields.clearToken', {}, 'Fjern eksisterende token')}
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('manager.modules.externalSystems.fields.docTypes', {}, 'Aktive flyter')}</label>
                  <div className="space-y-2 pt-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={formData.pickExport}
                        onChange={(e) => handleInputChange('pickExport', e.target.checked)} disabled={formLoading} />
                      {t('manager.modules.externalSystems.docTypes.pickExportLong', {}, 'Plukkeksport (send plukkordre)')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={formData.pickConfirmation}
                        onChange={(e) => handleInputChange('pickConfirmation', e.target.checked)} disabled={formLoading} />
                      {t('manager.modules.externalSystems.docTypes.pickConfirmationLong', {}, 'Plukkbekreftelse (hent ferdigmelding)')}
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>{t('manager.modules.externalSystems.fields.importPListStatus', {}, 'Avslutning ved ferdigmelding')}</label>
                  <select value={formData.importPListStatus}
                    onChange={(e) => handleInputChange('importPListStatus', e.target.value)}
                    disabled={formLoading} className={inputClass}>
                    <option value="45">{t('manager.modules.externalSystems.fields.status45', {}, '45 — Til pakking')}</option>
                    <option value="50">{t('manager.modules.externalSystems.fields.status50', {}, '50 — Klar for eksport (direkteavslutning)')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manager.modules.externalSystems.fields.status45Hint', {}, '45 krever konfigurert pakkelokasjon')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input type="checkbox" checked={formData.activeYesNo}
                    onChange={(e) => handleInputChange('activeYesNo', e.target.checked)} disabled={formLoading} />
                  {t('manager.modules.externalSystems.fields.active', {}, 'Aktiv')}
                </label>
                {editingId != null && (
                  <div>
                    <label className={labelClass}>{t('manager.modules.externalSystems.fields.confirmationNextId', {}, 'Bekreftelses-vannmerke')}</label>
                    <input type="number" value={formData.confirmationNextId}
                      onChange={(e) => handleInputChange('confirmationNextId', e.target.value)}
                      disabled={formLoading} className={inputClass} min={0} />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('manager.modules.externalSystems.fields.confirmationNextIdHint', {}, 'Justeres kun ved bevisst vedlikehold av polling-posisjonen')}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button type="button" onClick={() => setShowFormModal(false)} disabled={formLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {t('common.buttons.cancel', {}, 'Avbryt')}
                </button>
                <button type="submit" disabled={formLoading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {formLoading
                    ? t('common.labels.saving', {}, 'Lagrer...')
                    : t('common.buttons.save', {}, 'Lagre')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
